import { useCallback, useEffect, useRef, useState } from 'react'
import Lenis from 'lenis'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  ACT_RANGES,
  sceneStore,
  type Locale,
  type QualityTier,
} from '../lib/sceneStore'

gsap.registerPlugin(ScrollTrigger)

const DIRECTOR_TRIGGER_ID = 'cinematic-director'
const STANDARD_DEMO_DURATION = 60_000
const VERTICAL_DEMO_DURATION = 25_000
const VELOCITY_NORMALIZER = 2_400
const NAVIGATION_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'End',
  'Home',
  'PageDown',
  'PageUp',
  ' ',
  'Spacebar',
])

interface ConnectionHints extends EventTarget {
  readonly saveData?: boolean
}

interface NavigatorWithCapabilities extends Navigator {
  readonly connection?: ConnectionHints
  readonly deviceMemory?: number
}

interface QueryConfig {
  demoRequested: boolean
  verticalCapture: boolean
}

interface CapabilityState {
  quality: QualityTier
  reducedMotion: boolean
}

interface DirectorFrame {
  activeAct: number
  progress: number
}

interface DemoState {
  active: boolean
  paused: boolean
}

interface DemoCue {
  time: number
  progress: number
}

let cachedQueryConfig: QueryConfig | undefined

export interface CinematicDirector {
  activeAct: number
  progress: number
  demoRequested: boolean
  demoActive: boolean
  demoPaused: boolean
  verticalCapture: boolean
  reducedMotion: boolean
  quality: QualityTier
  toggleDemoPause: () => void
  restartDemo: () => void
  cancelDemo: () => void
}

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value))

const clamp01 = (value: number) => clamp(value, 0, 1)

const actPoint = (act: number, localProgress: number) => {
  const range = ACT_RANGES[act]
  return range.start + (range.end - range.start) * clamp01(localProgress)
}

const STANDARD_DEMO_CUES: readonly DemoCue[] = [
  { time: 0, progress: 0 },
  { time: 0.06, progress: actPoint(0, 0.58) },
  { time: 0.085, progress: actPoint(0, 0.58) },
  { time: 0.13, progress: actPoint(0, 1) },
  { time: 0.2, progress: actPoint(1, 1) },
  { time: 0.26, progress: actPoint(2, 1) },
  { time: 0.34, progress: actPoint(3, 0.64) },
  { time: 0.37, progress: actPoint(3, 0.64) },
  { time: 0.4, progress: actPoint(3, 1) },
  { time: 0.52, progress: actPoint(4, 1) },
  { time: 0.61, progress: actPoint(5, 0.78) },
  { time: 0.645, progress: actPoint(5, 0.78) },
  { time: 0.68, progress: actPoint(5, 1) },
  { time: 0.76, progress: actPoint(6, 1) },
  { time: 0.84, progress: actPoint(7, 0.7) },
  { time: 0.875, progress: actPoint(7, 0.7) },
  { time: 0.9, progress: actPoint(7, 1) },
  { time: 0.95, progress: actPoint(8, 0.62) },
  { time: 0.975, progress: actPoint(8, 0.62) },
  { time: 1, progress: 1 },
]

const VERTICAL_DEMO_CUES: readonly DemoCue[] = [
  { time: 0, progress: 0 },
  { time: 0.06, progress: actPoint(0, 0.58) },
  { time: 0.1, progress: actPoint(0, 0.58) },
  { time: 0.16, progress: actPoint(1, 0.72) },
  { time: 0.22, progress: actPoint(2, 0.72) },
  { time: 0.29, progress: actPoint(3, 0.64) },
  { time: 0.34, progress: actPoint(3, 0.64) },
  { time: 0.46, progress: actPoint(4, 0.33) },
  { time: 0.52, progress: actPoint(4, 0.64) },
  { time: 0.58, progress: actPoint(4, 0.92) },
  { time: 0.64, progress: actPoint(5, 0.78) },
  { time: 0.7, progress: actPoint(5, 0.78) },
  { time: 0.76, progress: actPoint(6, 0.65) },
  { time: 0.82, progress: actPoint(7, 0.65) },
  { time: 0.87, progress: actPoint(7, 0.65) },
  { time: 0.92, progress: actPoint(8, 0.62) },
  { time: 0.96, progress: actPoint(8, 0.62) },
  { time: 1, progress: 1 },
]

function sampleDemoProgress(cues: readonly DemoCue[], time: number) {
  const safeTime = clamp01(time)
  for (let index = 1; index < cues.length; index += 1) {
    const previous = cues[index - 1]
    const next = cues[index]
    if (safeTime > next.time) continue
    const segment = Math.max(0.0001, next.time - previous.time)
    const mix = clamp01((safeTime - previous.time) / segment)
    return previous.progress + (next.progress - previous.progress) * mix
  }
  return 1
}

function findDemoTime(cues: readonly DemoCue[], progress: number) {
  const safeProgress = clamp01(progress)
  for (let index = 1; index < cues.length; index += 1) {
    const previous = cues[index - 1]
    const next = cues[index]
    if (safeProgress > next.progress) continue
    const segment = next.progress - previous.progress
    if (segment <= 0.000001) return previous.time
    const mix = clamp01((safeProgress - previous.progress) / segment)
    return previous.time + (next.time - previous.time) * mix
  }
  return 1
}

function resolveWorldSegment(progress: number) {
  const range = ACT_RANGES[4]
  return Math.floor(
    clamp01((progress - range.start) / (range.end - range.start)) * 2.999,
  )
}

function readQueryConfig(): QueryConfig {
  const params = new URLSearchParams(window.location.search)

  return {
    demoRequested: params.get('demo') === '1',
    verticalCapture: params.get('capture') === 'vertical',
  }
}

function getQueryConfigOnce() {
  cachedQueryConfig ??= readQueryConfig()
  return cachedQueryConfig
}

function supportsWebGL2(): boolean {
  try {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('webgl2', {
      failIfMajorPerformanceCaveat: true,
      powerPreference: 'high-performance',
    })

    if (!context) return false

    context.getExtension('WEBGL_lose_context')?.loseContext()
    return true
  } catch {
    return false
  }
}

function readCapabilityState(webgl2Available: boolean): CapabilityState {
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches
  const nav = navigator as NavigatorWithCapabilities
  const saveData = nav.connection?.saveData === true
  const memory = nav.deviceMemory
  const cores = nav.hardwareConcurrency || 4
  const shortSide = Math.min(window.innerWidth, window.innerHeight)

  let quality: QualityTier = 'high'

  if (reducedMotion || saveData || !webgl2Available) {
    quality = 'poster'
  } else if (
    (memory !== undefined && memory <= 2) ||
    cores <= 2 ||
    shortSide <= 560
  ) {
    quality = 'lite'
  } else if (
    (memory !== undefined && memory <= 4) ||
    cores <= 4 ||
    window.innerWidth <= 1_100
  ) {
    quality = 'balanced'
  }

  return { quality, reducedMotion }
}

function isInteractiveTarget(target: EventTarget | null) {
  return (
    target instanceof Element &&
    Boolean(
      target.closest(
        'a, button, input, select, textarea, [contenteditable="true"], [role="button"]',
      ),
    )
  )
}

export function useCinematicDirector(locale: Locale): CinematicDirector {
  const [query] = useState<QueryConfig>(getQueryConfigOnce)
  const [webgl2Available] = useState(supportsWebGL2)
  const [capability, setCapability] = useState<CapabilityState>(() =>
    readCapabilityState(webgl2Available),
  )
  const initialFrame = sceneStore.read()
  const [frame, setFrame] = useState<DirectorFrame>({
    activeAct: initialFrame.activeAct,
    progress: initialFrame.progress,
  })
  const [demo, setDemo] = useState<DemoState>({
    active: false,
    paused: false,
  })

  const lenisRef = useRef<Lenis | null>(null)
  const uiFrameRef = useRef<DirectorFrame>(frame)
  const demoFrameRef = useRef<number | null>(null)
  const demoActiveRef = useRef(false)
  const demoPausedRef = useRef(false)
  const reducedMotionRef = useRef(capability.reducedMotion)
  const visibilityPausedRef = useRef(false)

  const updateDemoState = useCallback((active: boolean, paused: boolean) => {
    demoActiveRef.current = active
    demoPausedRef.current = paused
    setDemo((current) =>
      current.active === active && current.paused === paused
        ? current
        : { active, paused },
    )
  }, [])

  const stopDemoFrame = useCallback(() => {
    if (demoFrameRef.current === null) return
    window.cancelAnimationFrame(demoFrameRef.current)
    demoFrameRef.current = null
  }, [])

  const commitProgress = useCallback((progress: number, velocity: number) => {
    sceneStore.update({ progress: clamp01(progress), velocity: clamp(velocity, -1, 1) })
    const next = sceneStore.read()
    const current = uiFrameRef.current

    if (
      current.activeAct === next.activeAct &&
      (next.activeAct !== 4 ||
        resolveWorldSegment(current.progress) === resolveWorldSegment(next.progress))
    ) {
      return
    }

    const nextFrame = { activeAct: next.activeAct, progress: next.progress }
    uiFrameRef.current = nextFrame
    setFrame(nextFrame)
  }, [])

  const applyScrollProgress = useCallback(
    (progress: number) => {
      const nextProgress = clamp01(progress)
      const scrollLimit = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      )
      const target = scrollLimit * nextProgress

      if (lenisRef.current) {
        lenisRef.current.scrollTo(target, {
          force: true,
          immediate: true,
          programmatic: true,
        })
      } else {
        window.scrollTo({ top: target, behavior: 'auto' })
      }

      commitProgress(nextProgress, 0)
      ScrollTrigger.update()
    },
    [commitProgress],
  )

  const startDemoFrom = useCallback(
    (fromProgress: number) => {
      stopDemoFrame()

      if (reducedMotionRef.current) {
        updateDemoState(false, false)
        return
      }

      const startProgress = clamp01(fromProgress)
      if (startProgress >= 0.9999) {
        applyScrollProgress(1)
        updateDemoState(false, false)
        return
      }

      const totalDuration = query.verticalCapture
        ? VERTICAL_DEMO_DURATION
        : STANDARD_DEMO_DURATION
      const cues = query.verticalCapture
        ? VERTICAL_DEMO_CUES
        : STANDARD_DEMO_CUES
      const startTime = findDemoTime(cues, startProgress)
      const remainingDuration = Math.max(
        500,
        totalDuration * (1 - startTime),
      )
      const startedAt = performance.now()

      updateDemoState(true, false)

      const tick = (time: number) => {
        if (!demoActiveRef.current || demoPausedRef.current) return

        const elapsed = clamp01((time - startedAt) / remainingDuration)
        const demoTime = startTime + (1 - startTime) * elapsed
        const nextProgress = sampleDemoProgress(cues, demoTime)
        applyScrollProgress(nextProgress)

        if (elapsed >= 1) {
          demoFrameRef.current = null
          updateDemoState(false, false)
          return
        }

        demoFrameRef.current = window.requestAnimationFrame(tick)
      }

      demoFrameRef.current = window.requestAnimationFrame(tick)
    },
    [applyScrollProgress, query.verticalCapture, stopDemoFrame, updateDemoState],
  )

  const cancelDemo = useCallback(() => {
    stopDemoFrame()
    visibilityPausedRef.current = false
    sceneStore.update({ velocity: 0 })
    updateDemoState(false, false)
  }, [stopDemoFrame, updateDemoState])

  const toggleDemoPause = useCallback(() => {
    if (reducedMotionRef.current) return

    if (!demoActiveRef.current) {
      startDemoFrom(sceneStore.read().progress)
      return
    }

    if (demoPausedRef.current) {
      visibilityPausedRef.current = false
      startDemoFrom(sceneStore.read().progress)
      return
    }

    stopDemoFrame()
    sceneStore.update({ velocity: 0 })
    updateDemoState(true, true)
  }, [startDemoFrom, stopDemoFrame, updateDemoState])

  const restartDemo = useCallback(() => {
    if (reducedMotionRef.current) return

    visibilityPausedRef.current = false
    applyScrollProgress(0)
    startDemoFrom(0)
  }, [applyScrollProgress, startDemoFrom])

  useEffect(() => {
    sceneStore.update({ locale })
  }, [locale])

  useEffect(() => {
    reducedMotionRef.current = capability.reducedMotion
    sceneStore.update({
      locale,
      quality: capability.quality,
      reducedMotion: capability.reducedMotion,
      verticalCapture: query.verticalCapture,
    })
  }, [capability, locale, query.verticalCapture])

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const connection = (navigator as NavigatorWithCapabilities).connection
    let resizeFrame: number | null = null

    const recompute = () => {
      resizeFrame = null
      const next = readCapabilityState(webgl2Available)
      setCapability((current) =>
        current.quality === next.quality &&
        current.reducedMotion === next.reducedMotion
          ? current
          : next,
      )
    }

    const scheduleRecompute = () => {
      if (resizeFrame !== null) return
      resizeFrame = window.requestAnimationFrame(recompute)
    }

    media.addEventListener('change', scheduleRecompute)
    connection?.addEventListener('change', scheduleRecompute)
    window.addEventListener('resize', scheduleRecompute, { passive: true })
    window.addEventListener('orientationchange', scheduleRecompute, {
      passive: true,
    })

    return () => {
      if (resizeFrame !== null) window.cancelAnimationFrame(resizeFrame)
      media.removeEventListener('change', scheduleRecompute)
      connection?.removeEventListener('change', scheduleRecompute)
      window.removeEventListener('resize', scheduleRecompute)
      window.removeEventListener('orientationchange', scheduleRecompute)
    }
  }, [webgl2Available])

  useEffect(() => {
    ScrollTrigger.getById(DIRECTOR_TRIGGER_ID)?.kill()

    let velocityTimer: number | null = null
    const trigger = ScrollTrigger.create({
      id: DIRECTOR_TRIGGER_ID,
      start: 0,
      end: () => Math.max(1, ScrollTrigger.maxScroll(window)),
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        commitProgress(
          self.progress,
          self.getVelocity() / VELOCITY_NORMALIZER,
        )

        if (velocityTimer !== null) window.clearTimeout(velocityTimer)
        velocityTimer = window.setTimeout(() => {
          sceneStore.update({ velocity: 0 })
          velocityTimer = null
        }, 140)
      },
    })

    if (!capability.reducedMotion) {
      const lenis = new Lenis({
        anchors: false,
        autoRaf: false,
        duration: 1.05,
        smoothWheel: true,
        syncTouch: false,
      })
      const updateScrollTrigger = () => ScrollTrigger.update()
      const driveLenis = (time: number) => lenis.raf(time * 1_000)

      lenisRef.current = lenis
      const unsubscribe = lenis.on('scroll', updateScrollTrigger)
      gsap.ticker.add(driveLenis)

      const refreshFrame = window.requestAnimationFrame(() => {
        lenis.resize()
        ScrollTrigger.refresh()
      })

      return () => {
        window.cancelAnimationFrame(refreshFrame)
        if (velocityTimer !== null) window.clearTimeout(velocityTimer)
        unsubscribe()
        gsap.ticker.remove(driveLenis)
        trigger.kill()
        lenis.destroy()
        if (lenisRef.current === lenis) lenisRef.current = null
      }
    }

    lenisRef.current = null
    const refreshFrame = window.requestAnimationFrame(() =>
      ScrollTrigger.refresh(),
    )

    return () => {
      window.cancelAnimationFrame(refreshFrame)
      if (velocityTimer !== null) window.clearTimeout(velocityTimer)
      trigger.kill()
    }
  }, [capability.reducedMotion, commitProgress])

  useEffect(() => {
    const setPointer = (event: PointerEvent) => {
      sceneStore.update({
        pointerActive: true,
        pointerX: clamp((event.clientX / window.innerWidth) * 2 - 1, -1, 1),
        pointerY: clamp(1 - (event.clientY / window.innerHeight) * 2, -1, 1),
      })
    }
    const clearPointer = () => sceneStore.update({ pointerActive: false })
    const releaseTouchPointer = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') clearPointer()
    }

    window.addEventListener('pointerdown', setPointer, { passive: true })
    window.addEventListener('pointermove', setPointer, { passive: true })
    window.addEventListener('pointerup', releaseTouchPointer, { passive: true })
    window.addEventListener('pointercancel', clearPointer, { passive: true })
    window.addEventListener('blur', clearPointer)
    document.documentElement.addEventListener('pointerleave', clearPointer)

    return () => {
      window.removeEventListener('pointerdown', setPointer)
      window.removeEventListener('pointermove', setPointer)
      window.removeEventListener('pointerup', releaseTouchPointer)
      window.removeEventListener('pointercancel', clearPointer)
      window.removeEventListener('blur', clearPointer)
      document.documentElement.removeEventListener('pointerleave', clearPointer)
    }
  }, [])

  useEffect(() => {
    const handleWheel = () => {
      if (demoActiveRef.current) cancelDemo()
    }
    const handleTouch = (event: TouchEvent) => {
      if (demoActiveRef.current && !isInteractiveTarget(event.target)) {
        cancelDemo()
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      const hasModifier = event.altKey || event.ctrlKey || event.metaKey
      const interactive = isInteractiveTarget(event.target)
      const key = event.key.toLowerCase()

      if (query.demoRequested && !hasModifier && !interactive) {
        if (key === 'p') {
          event.preventDefault()
          toggleDemoPause()
          return
        }
        if (key === 'r') {
          event.preventDefault()
          restartDemo()
          return
        }
        if (event.key === 'Escape' && demoActiveRef.current) {
          cancelDemo()
          return
        }
      }

      const activationSpace =
        interactive &&
        (event.key === ' ' || event.key === 'Spacebar')
      if (
        demoActiveRef.current &&
        NAVIGATION_KEYS.has(event.key) &&
        !hasModifier &&
        !activationSpace
      ) {
        cancelDemo()
      }
    }
    const handleAnchorClick = (event: MouseEvent) => {
      if (
        demoActiveRef.current &&
        event.target instanceof Element &&
        event.target.closest('a[href^="#"]')
      ) {
        cancelDemo()
      }
    }
    const handleScrollbarPointer = (event: PointerEvent) => {
      if (
        demoActiveRef.current &&
        event.clientX >= document.documentElement.clientWidth
      ) {
        cancelDemo()
      }
    }

    window.addEventListener('wheel', handleWheel, {
      capture: true,
      passive: true,
    })
    window.addEventListener('touchstart', handleTouch, {
      capture: true,
      passive: true,
    })
    window.addEventListener('keydown', handleKey, true)
    window.addEventListener('click', handleAnchorClick, true)
    window.addEventListener('pointerdown', handleScrollbarPointer, {
      capture: true,
      passive: true,
    })

    return () => {
      window.removeEventListener('wheel', handleWheel, true)
      window.removeEventListener('touchstart', handleTouch, true)
      window.removeEventListener('keydown', handleKey, true)
      window.removeEventListener('click', handleAnchorClick, true)
      window.removeEventListener('pointerdown', handleScrollbarPointer, true)
    }
  }, [cancelDemo, query.demoRequested, restartDemo, toggleDemoPause])

  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.hidden &&
        demoActiveRef.current &&
        !demoPausedRef.current
      ) {
        stopDemoFrame()
        visibilityPausedRef.current = true
        updateDemoState(true, true)
        return
      }

      if (!document.hidden && visibilityPausedRef.current) {
        visibilityPausedRef.current = false
        startDemoFrom(sceneStore.read().progress)
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibility)
  }, [startDemoFrom, stopDemoFrame, updateDemoState])

  useEffect(() => {
    if (!query.demoRequested || capability.reducedMotion) {
      const cancelFrame = window.requestAnimationFrame(cancelDemo)
      return () => window.cancelAnimationFrame(cancelFrame)
    }

    let startFrame = window.requestAnimationFrame(() => {
      ScrollTrigger.refresh()
      startFrame = window.requestAnimationFrame(restartDemo)
    })

    return () => {
      window.cancelAnimationFrame(startFrame)
      stopDemoFrame()
      demoActiveRef.current = false
      demoPausedRef.current = false
      visibilityPausedRef.current = false
    }
  }, [
    cancelDemo,
    capability.reducedMotion,
    query.demoRequested,
    restartDemo,
    stopDemoFrame,
  ])

  return {
    activeAct: frame.activeAct,
    progress: frame.progress,
    demoRequested: query.demoRequested,
    demoActive: demo.active,
    demoPaused: demo.paused,
    verticalCapture: query.verticalCapture,
    reducedMotion: capability.reducedMotion,
    quality: capability.quality,
    toggleDemoPause,
    restartDemo,
    cancelDemo,
  }
}
